import fieldsSectionized from 'pages/DynamicOCA/config/fields_sectionized.json';

// Mirrors the field option shape used by the OCA form builder (see CustomizationPanel.js)
interface OcaFieldOption {
  optionValue: unknown;
  optionLabel?: string;
  displayLabel?: { en?: string; es?: string };
}

interface OcaFieldConfig {
  fieldName?: string;
  fieldOptions?: OcaFieldOption[];
}

interface OcaField {
  type: string; // select-field, amount-field, phone-field, text-field, typed-field, etc.
  config: OcaFieldConfig;
}

interface OcaFieldSection {
  fieldList?: OcaField[];
}

const FIELD_BY_NAME = new Map<string, OcaField>();
(fieldsSectionized as OcaFieldSection[]).forEach(section => {
  (section.fieldList ?? []).forEach(field => {
    const name = field.config?.fieldName;
    if (name && !FIELD_BY_NAME.has(name)) {
      FIELD_BY_NAME.set(name, field);
    }
  });
});

export interface FieldSelectOption {
  label: string;
  value: string;
}

// The OCA select's displayLabel doesn't always carry the unit shown alongside the input in the form
const SELECT_LABEL_SUFFIX_BY_FIELD_NAME: Record<string, string> = {
  termRequested: ' months',
};

const SELECT_FIELD_TYPES = ['select-field', 'autocomplete-field'];

// Returns the dropdown options the OCA form used to capture this field, so an edit UI can
// reproduce the same choices instead of a free-text input
export function getOcaFieldSelectOptions(fieldName: string): FieldSelectOption[] | undefined {
  const field = FIELD_BY_NAME.get(fieldName);
  const options = field?.config?.fieldOptions;
  if (!field || !options?.length || !SELECT_FIELD_TYPES.includes(field.type)) return undefined;

  const suffix = SELECT_LABEL_SUFFIX_BY_FIELD_NAME[fieldName] ?? '';
  return options.map(option => ({
    value: String(option.optionValue),
    label: `${option.displayLabel?.en || option.optionLabel || String(option.optionValue)}${suffix}`,
  }));
}

function formatPhone(value: unknown): string {
  const digits = String(value).replace(/[^0-9]/g, '');
  return digits.length === 10 ? `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}` : String(value);
}

function formatCurrency(value: unknown): string {
  const amount = Number(value);
  return Number.isNaN(amount)
    ? String(value)
    : `$${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// Formats an extracted value the same way the OCA form field that captured it would display it —
// e.g. a select-field shows its option's displayLabel, an amount-field shows currency, etc.
export function formatOcaFieldValue(fieldName: string, value: unknown): string {
  if (value == null || value === '') return '';

  const options = getOcaFieldSelectOptions(fieldName);
  if (options) {
    return options.find(option => option.value === String(value))?.label ?? String(value);
  }

  switch (FIELD_BY_NAME.get(fieldName)?.type) {
    case 'amount-field':
      return formatCurrency(value);
    case 'phone-field':
      return formatPhone(value);
    default:
      return String(value);
  }
}
